import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('wne_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Auth endpoints
  login: async (credentials) => {
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  verifyAuth: async () => {
    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Auth verification error:', error);
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('wne_admin_token');
    localStorage.removeItem('wne_admin_email');
  },

  // Config endpoints
  getConfig: async () => {
    try {
      const response = await axios.get(`${API}/config`);
      return response.data;
    } catch (error) {
      console.error('Error fetching config:', error);
      throw error;
    }
  },

  updateConfig: async (configData) => {
    try {
      const token = localStorage.getItem('wne_admin_token');
      const response = await axios.put(`${API}/config`, configData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating config:', error);
      throw error;
    }
  },

  // Analytics endpoints (protected)
  trackEvent: async (eventData) => {
    try {
      const response = await axios.post(`${API}/analytics/track`, eventData);
      return response.data;
    } catch (error) {
      console.error('Error tracking event:', error);
      // Don't throw - analytics should fail silently
      return { success: false };
    }
  },

  getAnalytics: async (days = 7) => {
    try {
      const response = await axios.get(`${API}/analytics/stats?days=${days}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  },

  // Lead capture endpoints
  captureLead: async (leadData) => {
    try {
      const response = await axios.post(`${API}/leads/capture`, leadData);
      return response.data;
    } catch (error) {
      console.error('Error capturing lead:', error);
      throw error;
    }
  },

  getLeads: async () => {
    try {
      const response = await axios.get(`${API}/leads/list`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching leads:', error);
      throw error;
    }
  },

  // Link management endpoints (protected)
  createLink: async (linkData) => {
    try {
      const response = await axios.post(`${API}/links`, linkData, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating link:', error);
      throw error;
    }
  },

  updateLink: async (linkId, linkData) => {
    try {
      const response = await axios.put(`${API}/links/${linkId}`, linkData, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating link:', error);
      throw error;
    }
  },

  deleteLink: async (linkId) => {
    try {
      const response = await axios.delete(`${API}/links/${linkId}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting link:', error);
      throw error;
    }
  },

  reorderLinks: async (linkIds) => {
    try {
      const response = await axios.post(`${API}/links/reorder`, linkIds, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error reordering links:', error);
      throw error;
    }
  },

  // Contact management endpoints (protected)
  createContact: async (contactData) => {
    try {
      const response = await axios.post(`${API}/contacts`, contactData, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  },

  updateContact: async (contactId, contactData) => {
    try {
      const response = await axios.put(`${API}/contacts/${contactId}`, contactData, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  },

  deleteContact: async (contactId) => {
    try {
      const response = await axios.delete(`${API}/contacts/${contactId}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  },

  reorderContacts: async (contactIds) => {
    try {
      const response = await axios.post(`${API}/contacts/reorder`, contactIds, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error reordering contacts:', error);
      throw error;
    }
  }
};
